import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  MenuItem,
  Chip
} from '@mui/material';
import { Add, Edit, Delete, People } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    nom_user: '',
    prenom_user: '',
    login: '',
    mdp: '',
    role_id: 3 // Secrétaire par défaut
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const roles = [
    { id: 2, name: 'Vétérinaire' },
    { id: 3, name: 'Secrétaire' },
    { id: 4, name: 'Technicien' }
  ];

  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des utilisateurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditMode(true);
      setCurrentUser(user);
      setFormData({
        nom_user: user.nom_user || '',
        prenom_user: user.prenom_user || '',
        login: user.login || '',
        mdp: '',
        role_id: user.role_id || 3
      });
    } else {
      setEditMode(false);
      setCurrentUser(null);
      setFormData({
        nom_user: '',
        prenom_user: '',
        login: '',
        mdp: '',
        role_id: 3
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentUser(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editMode && currentUser) {
        await axios.put(
          `${API_URL}/admin/users/${currentUser.id_user}`,
          formData,
          config
        );
        showSnackbar('Utilisateur modifié avec succès', 'success');
      } else {
        await axios.post(`${API_URL}/admin/users`, formData, config);
        showSnackbar('Utilisateur créé avec succès', 'success');
      }
      handleCloseDialog();
      loadUsers();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('Utilisateur supprimé avec succès', 'success');
      loadUsers();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur lors de la suppression', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : 'Inconnu';
  };

  const getRoleColor = (roleId) => {
    switch (roleId) {
      case 2: return 'error';
      case 3: return 'primary';
      case 4: return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <People sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestion des Utilisateurs
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nouvel Utilisateur
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Nom</strong></TableCell>
              <TableCell><strong>Prénom</strong></TableCell>
              <TableCell><strong>Login</strong></TableCell>
              <TableCell><strong>Rôle</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    Aucun utilisateur trouvé. Cliquez sur "Nouvel Utilisateur" pour en ajouter un.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id_user} hover>
                  <TableCell>{user.id_user}</TableCell>
                  <TableCell><strong>{user.nom_user}</strong></TableCell>
                  <TableCell>{user.prenom_user}</TableCell>
                  <TableCell>{user.login}</TableCell>
                  <TableCell>
                    <Chip 
                      label={user.Role?.description_role || getRoleName(user.role_id)}
                      color={getRoleColor(user.role_id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      color="primary" 
                      size="small"
                      onClick={() => handleOpenDialog(user)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton 
                      color="error" 
                      size="small"
                      onClick={() => handleDelete(user.id_user)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog d'ajout/modification */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              name="nom_user"
              label="Nom"
              fullWidth
              margin="normal"
              value={formData.nom_user}
              onChange={handleChange}
              required
            />
            <TextField
              name="prenom_user"
              label="Prénom"
              fullWidth
              margin="normal"
              value={formData.prenom_user}
              onChange={handleChange}
              required
            />
            <TextField
              name="login"
              label="Login"
              fullWidth
              margin="normal"
              value={formData.login}
              onChange={handleChange}
              required
              disabled={editMode}
            />
            <TextField
              name="mdp"
              label={editMode ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe"}
              type="password"
              fullWidth
              margin="normal"
              value={formData.mdp}
              onChange={handleChange}
              required={!editMode}
            />
            <TextField
              name="role_id"
              label="Rôle"
              fullWidth
              margin="normal"
              value={formData.role_id}
              onChange={handleChange}
              required
              select
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UsersManagement;